import React, {
  ReactNode,
  memo,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import StyledPopover from './StyledPopover';
import { PopoverState, PopoverController, PopoverPosition } from '.';
import {
  registerClickOutsideHandler,
  registerKeyDownHandler,
  buildRepositionFunction,
} from './helpers';
import { noop } from '../../util';

interface PopoverProps {
  initialState?: PopoverState;
  isCloseOnESC?: boolean;
  isCloseOnClickOutside?: boolean;
  onStateChange?: (nextState: PopoverState) => void;
  onController?: (controller: PopoverController) => void;
  children?: ReactNode;
  popoverPosition?: PopoverPosition;
}

function Popover({
  initialState,
  isCloseOnESC,
  isCloseOnClickOutside,
  onStateChange,
  onController,
  children,
  popoverPosition,
}: PopoverProps): JSX.Element {
  const styledPopoverRef = useRef(null);
  const [state, setState] = useState<PopoverState>(
    initialState || {
      isShow: false,
      referedElement: null,
    },
  );

  const hidePopover = useMemo(
    () => (): void => {
      const nextState = {
        isShow: false,
        referedElement: null,
      };
      setState(nextState);
      onStateChange(nextState);
    },
    [onStateChange],
  );
  const showPopover = useMemo(
    () => (referedElement: HTMLElement): void => {
      const nextState = {
        isShow: true,
        referedElement,
      };
      setState(nextState);
      onStateChange(nextState);
    },
    [onStateChange],
  );
  const reposition = useMemo(
    () => buildRepositionFunction(state, styledPopoverRef, popoverPosition),
    [popoverPosition, state],
  );

  useEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    onController({
      showPopover,
      hidePopover,
      reposition,
    });
  }, [hidePopover, onController, reposition, showPopover]);

  useEffect(() => {
    const cleanUpFunctions = [
      isCloseOnESC
        ? registerKeyDownHandler(hidePopover, styledPopoverRef)
        : null,
      isCloseOnClickOutside
        ? registerClickOutsideHandler(hidePopover, styledPopoverRef)
        : null,
    ];

    return (): void => {
      cleanUpFunctions.forEach((func) => func && func());
    };
  }, [hidePopover, isCloseOnESC, isCloseOnClickOutside]);

  return (
    state.isShow && (
      <StyledPopover ref={styledPopoverRef}>{children}</StyledPopover>
    )
  );
}

Popover.defaultProps = {
  initialState: {
    isShow: false,
  },
  isCloseOnESC: true,
  isCloseOnClickOutside: true,
  onStateChange: noop,
  onController: noop,
};

export default memo(Popover);
